import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Save, X, Trash2, Eye, EyeOff, Loader2, Search, Check } from 'lucide-react';
import { apiClient } from '../../../lib/apiClient';
import { resolveMediaUrl } from '../../../lib/mediaUrl';
import PasteImageUpload from '../../../components/admin/PasteImageUpload';
import { apiError } from './apiError';

const MAX_IMAGES = 10; // mirrors MAX_IMAGES in backend/directory.py, which enforces it

const EMPTY = {
  name: '',
  category_id: '',
  phone: '',
  address: '',
  hours: '',
  services: [],
  images: [],
  published: true,
  slug: '',
};

const inputClass =
  'w-full px-3 py-2 min-h-[44px] rounded-lg bg-gray-50 border border-gray-200 text-gray-900 text-sm focus:border-[#FF6B00] outline-none';

/**
 * Create, edit, publish and delete directory listings.
 *
 * `mode="pending"` shows public "Add a business" submissions instead: the admin
 * can fix details and add images, then Approve (goes live) or Reject (deletes).
 * Same editor, so there is one form to maintain.
 */
const ListingsPanel = ({ mode = 'approved', onChanged }) => {
  const pendingMode = mode === 'pending';
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [serviceDraft, setServiceDraft] = useState('');

  const loadCategories = useCallback(async () => {
    try {
      const res = await apiClient.get('/directory/categories');
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(apiError(err, 'Could not load categories.'));
    }
  }, []);

  const loadListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (query.trim()) params.q = query.trim();
      if (categoryFilter) params.category_id = categoryFilter;
      if (pendingMode) params.status = 'pending';
      const res = await apiClient.get('/directory/listings', { params });
      setListings(Array.isArray(res.data) ? res.data : []);
      setError(null);
    } catch (err) {
      setError(apiError(err, 'Could not load listings.'));
    } finally {
      setLoading(false);
    }
  }, [query, categoryFilter, pendingMode]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  // Debounced so typing in the search box does not fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(loadListings, 250);
    return () => clearTimeout(t);
  }, [loadListings]);

  const categoryName = (id) => categories.find((c) => c.id === id)?.name;

  const openEditor = (listing) => {
    setServiceDraft('');
    setEditing(listing);
  };

  const addService = () => {
    const value = serviceDraft.trim();
    if (!value) return;
    if (!editing.services.some((s) => s.toLowerCase() === value.toLowerCase())) {
      setEditing({ ...editing, services: [...editing.services, value] });
    }
    setServiceDraft('');
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const body = {
        name: editing.name,
        category_id: editing.category_id || null,
        phone: editing.phone,
        address: editing.address,
        hours: editing.hours,
        services: editing.services,
        images: editing.images,
        published: editing.published,
        slug: editing.slug || '',
      };
      if (editing.id) await apiClient.put(`/directory/listings/${editing.id}`, body);
      else await apiClient.post('/directory/listings', body);
      setEditing(null);
      await Promise.all([loadListings(), loadCategories()]);
      onChanged?.();
    } catch (err) {
      setError(apiError(err, 'Could not save the listing.'));
    } finally {
      setSaving(false);
    }
  };

  const togglePublished = async (l) => {
    try {
      await apiClient.patch(`/directory/listings/${l.id}/publish`, null, { params: { published: !l.published } });
      setListings((prev) => prev.map((x) => (x.id === l.id ? { ...x, published: !l.published } : x)));
    } catch (err) {
      setError(apiError(err, `Could not update "${l.name}".`));
    }
  };

  const remove = async (l) => {
    const prompt = pendingMode
      ? `Reject "${l.name}"? The submission will be deleted. This cannot be undone.`
      : `Delete "${l.name}" and its images? This cannot be undone.`;
    if (!window.confirm(prompt)) return;
    try {
      await apiClient.delete(`/directory/listings/${l.id}`);
      setListings((prev) => prev.filter((x) => x.id !== l.id));
      loadCategories();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, `Could not ${pendingMode ? 'reject' : 'delete'} "${l.name}".`));
    }
  };

  const approve = async (l) => {
    try {
      await apiClient.post(`/directory/listings/${l.id}/approve`);
      setListings((prev) => prev.filter((x) => x.id !== l.id));
      loadCategories();
      onChanged?.();
    } catch (err) {
      setError(apiError(err, `Could not approve "${l.name}".`));
    }
  };

  const setField = (key) => (e) => setEditing({ ...editing, [key]: e.target.value });

  return (
    <div data-testid="directory-listings">
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search listings by name"
            aria-label="Search listings by name"
            className="w-full pl-9 pr-3 py-2 min-h-[44px] rounded-lg bg-white border border-gray-200 text-sm focus:border-[#FF6B00] outline-none"
            data-testid="listing-search"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          aria-label="Filter by category"
          className="px-3 py-2 min-h-[44px] rounded-lg bg-white border border-gray-200 text-sm focus:border-[#FF6B00] outline-none"
        >
          <option value="">All categories</option>
          {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        {!pendingMode && (
          <button
            type="button"
            onClick={() => openEditor({ ...EMPTY, services: [], images: [] })}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 min-h-[44px] rounded-lg bg-[#FF6B00] hover:bg-[#ff8124] text-[#1a1c1b] font-semibold text-sm"
            data-testid="listing-add"
          >
            <Plus className="w-4 h-4" /> Add listing
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-red-600 text-sm" role="alert">{error}</div>
      )}

      {editing && (
        <form onSubmit={save} className="mb-6 p-4 sm:p-5 rounded-xl border border-[#FF6B00]/40 bg-white" data-testid="listing-editor">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-gray-900 font-semibold">{editing.id ? `Edit ${editing.name}` : 'New listing'}</h2>
            <button type="button" onClick={() => setEditing(null)} aria-label="Close editor" className="p-2 text-gray-500 hover:text-gray-900">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-3 mb-3">
            <div>
              <label htmlFor="l-name" className="block text-gray-500 text-xs mb-1">Name *</label>
              <input id="l-name" required maxLength={200} value={editing.name} onChange={setField('name')} className={inputClass} data-testid="listing-name" />
            </div>
            <div>
              <label htmlFor="l-cat" className="block text-gray-500 text-xs mb-1">Category</label>
              <select id="l-cat" value={editing.category_id || ''} onChange={setField('category_id')} className={inputClass} data-testid="listing-category">
                <option value="">No category</option>
                {categories
                  .filter((c) => c.active || c.id === editing.category_id)
                  .map((c) => <option key={c.id} value={c.id}>{c.name}{c.active ? '' : ' (inactive)'}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="l-phone" className="block text-gray-500 text-xs mb-1">Phone</label>
              <input id="l-phone" type="tel" maxLength={40} value={editing.phone} onChange={setField('phone')} className={inputClass} data-testid="listing-phone" />
            </div>
            <div>
              <label htmlFor="l-address" className="block text-gray-500 text-xs mb-1">Address</label>
              <input id="l-address" maxLength={300} value={editing.address} onChange={setField('address')} className={inputClass} data-testid="listing-address" />
            </div>
          </div>

          <div className="mb-3">
            <label htmlFor="l-hours" className="block text-gray-500 text-xs mb-1">Hours</label>
            <textarea id="l-hours" rows={3} maxLength={500} value={editing.hours} onChange={setField('hours')} placeholder={'Mon-Fri 11am-2pm\nSat 9am-12pm'} className={`${inputClass} resize-none`} data-testid="listing-hours" />
          </div>

          <div className="mb-3">
            <label htmlFor="l-slug" className="block text-gray-500 text-xs mb-1">Page address (slug)</label>
            <input
              id="l-slug"
              value={editing.slug || ''}
              maxLength={80}
              onChange={(e) => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
              placeholder="Leave blank to build it from name, city and state"
              className={inputClass}
              data-testid="listing-slug"
            />
            <p className="text-gray-500 text-xs mt-1">
              /directory/{editing.slug || 'business-name-city-state'}
              {editing.id ? ' · Changing this keeps the old address working with a redirect.' : ''}
            </p>
          </div>

          <div className="mb-3">
            <label htmlFor="l-service" className="block text-gray-500 text-xs mb-1">Services offered</label>
            <div className="flex gap-2">
              <input
                id="l-service"
                value={serviceDraft}
                maxLength={80}
                onChange={(e) => setServiceDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addService(); } }}
                placeholder="Type a service and press Enter, e.g. Hot meals"
                className={inputClass}
                data-testid="listing-service-input"
              />
              <button type="button" onClick={addService} className="px-4 min-h-[44px] rounded-lg border border-gray-200 text-gray-700 text-sm hover:border-[#FF6B00] hover:text-[#FF6B00]">Add</button>
            </div>
            {editing.services.length > 0 && (
              <ul className="flex flex-wrap gap-2 mt-2">
                {editing.services.map((s) => (
                  <li key={s} className="inline-flex items-center gap-1 pl-3 pr-1 py-1 rounded-full bg-orange-50 border border-orange-200 text-sm text-gray-800">
                    {s}
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, services: editing.services.filter((x) => x !== s) })}
                      aria-label={`Remove ${s}`}
                      className="p-1.5 text-gray-500 hover:text-red-600"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mb-4">
            <PasteImageUpload
              label="Images"
              folder="directory"
              max={MAX_IMAGES}
              value={editing.images}
              onChange={(images) => setEditing({ ...editing, images })}
            />
          </div>

          <div className="flex flex-wrap items-center gap-4">
            <button
              type="submit"
              disabled={saving || !editing.name.trim()}
              className="inline-flex items-center gap-2 px-4 py-2 min-h-[44px] rounded-lg bg-[#FF6B00] hover:bg-[#ff8124] disabled:opacity-50 text-[#1a1c1b] font-semibold text-sm"
              data-testid="listing-save"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
            {pendingMode ? (
              <span className="text-sm text-amber-700">Pending review. Approve it from the list to publish.</span>
            ) : (
              <label className="flex items-center gap-2 text-gray-700 text-sm cursor-pointer min-h-[44px]">
                <input type="checkbox" className="accent-[#FF6B00] w-4 h-4" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />
                Published
              </label>
            )}
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
      ) : listings.length === 0 ? (
        <p className="text-gray-500 text-sm py-8 text-center">
          {query || categoryFilter
            ? 'No listings match.'
            : pendingMode ? 'No submissions waiting for review.' : 'No listings yet. Add the first one above.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {listings.map((l) => (
            <li key={l.id} className="flex items-center gap-2 sm:gap-3 p-3 rounded-xl border border-gray-200 bg-white" data-testid={`listing-row-${l.id}`}>
              {l.images?.[0] ? (
                <img src={resolveMediaUrl(l.images[0])} alt="" className="w-14 h-14 sm:w-16 sm:h-12 object-cover rounded border border-gray-200 flex-shrink-0" />
              ) : (
                <div className="w-14 h-14 sm:w-16 sm:h-12 rounded border border-gray-200 bg-gray-50 flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-gray-900 text-sm font-semibold break-words sm:truncate">{l.name}</p>
                <p className="text-gray-500 text-xs break-words sm:truncate">
                  {[categoryName(l.category_id), l.phone, l.address].filter(Boolean).join(' · ') || 'No details yet'}
                </p>
                {pendingMode
                  ? <p className="text-xs text-amber-600">Submitted from the website</p>
                  : !l.published && <p className="text-xs text-amber-600">Unpublished</p>}
              </div>
              {pendingMode ? (
                <button type="button" onClick={() => approve(l)} className="inline-flex items-center gap-1 px-3 py-2 min-h-[44px] rounded-lg bg-[#FF6B00] hover:bg-[#ff8124] text-[#1a1c1b] text-xs font-semibold" data-testid={`listing-approve-${l.id}`}>
                  <Check className="w-4 h-4" /> Approve
                </button>
              ) : (
                <button type="button" onClick={() => togglePublished(l)} aria-pressed={l.published} aria-label={`${l.published ? 'Unpublish' : 'Publish'} ${l.name}`} className={`p-2 ${l.published ? 'text-[#FF6B00]' : 'text-gray-500 hover:text-gray-900'}`}>
                  {l.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                </button>
              )}
              <button type="button" onClick={() => openEditor({ ...l, category_id: l.category_id || '' })} className="px-3 py-2 min-h-[44px] rounded-lg border border-gray-200 text-gray-700 text-xs hover:border-[#FF6B00] hover:text-[#FF6B00]">Edit</button>
              <button type="button" onClick={() => remove(l)} aria-label={`${pendingMode ? 'Reject' : 'Delete'} ${l.name}`} className="p-2 text-gray-500 hover:text-red-600" data-testid={`listing-remove-${l.id}`}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default ListingsPanel;
